/**
 * bridge/open_settings_controller.js — "open-settings" bridge component
 * (Stimulus + Strada).
 *
 * Deep-links the user into this app's own page in the OS settings app. Stimulus
 * identifier "bridge--open-settings" (glob-derived from this subdir); Strada
 * component name "open-settings" MUST match the iOS class var name
 * (native/ios/Chippie/Bridge/OpenSettingsComponent.swift).
 *
 * Why this exists: it is the recovery path for every other permission-gated
 * component. Once a user taps "Don't Allow" on camera, location, notifications
 * or contacts, iOS never asks again and Android stops asking after two refusals
 * — the ONLY way back is the OS settings screen. Without this component the app
 * can detect a denied permission (bridge--permissions) but cannot do anything
 * about it beyond telling the user to go find it themselves.
 *
 * API — window.ChippieBridge.openSettings(section) -> Boolean (did we open it):
 *   section "app"           (default) the app's own settings page
 *   section "notifications" the app's notification settings, where supported
 * Unknown sections fall back to "app" rather than throwing; this is called from
 * server-driven markup, so a typo should degrade, not break the page.
 *
 * Three-way transport (facade SSOT, chippie-bridge.js owns platform detection):
 *   - ios:     this.send("open", { section }) -> UIApplication.openSettingsURLString
 *              (or openNotificationSettingsURLString on iOS 16+).
 *   - android: window.ChippieNative.openAppSettings(section) -> an
 *              ACTION_APPLICATION_DETAILS_SETTINGS / ACTION_APP_NOTIFICATION_SETTINGS
 *              intent.
 *   - pwa:     returns false. No browser API can open OS settings, and there is
 *              no honest approximation — the caller should render instructions
 *              instead of assuming the user was taken somewhere.
 *
 * Server-drivable (listed in REMOTE_ALLOWED / BRIDGE_REMOTE_CALLS): opening
 * settings needs no permission of its own and raises no OS prompt. It leaves the
 * app, so keep it behind an explicit "Fix this" affordance rather than firing it
 * unprompted at a user mid-task.
 *
 * Template wiring (one global instance in the shell is enough):
 *   <div data-controller="bridge--open-settings"></div>
 * Declarative trigger, no JS:
 *   <button data-controller="bridge--invoke"
 *           data-bridge-call="openSettings"
 *           data-bridge-args='["notifications"]'>Enable notifications</button>
 *
 * Outside a native shell BridgeComponent never connect()s, so the Android/PWA
 * impls are wired standalone at module load and connect() upgrades iOS.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

const SECTIONS = ["app", "notifications"];

function section(name) {
    return SECTIONS.indexOf(name) === -1 ? "app" : name;
}

function androidOpenSettings(name) {
    if (!window.ChippieNative || !window.ChippieNative.openAppSettings) return false;
    try {
        window.ChippieNative.openAppSettings(section(name));
        return true;
    } catch (_e) {
        return false;
    }
}

// A browser cannot reach OS settings. Returning false (rather than silently
// no-opping) lets the caller fall back to on-screen instructions.
function pwaOpenSettings() {
    return false;
}

function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("openSettings", {
        // ios is overridden by connect() with a real send; this placeholder is a
        // safe "did nothing" if somehow selected before connect.
        ios: () => false,
        android: androidOpenSettings,
        pwa: pwaOpenSettings,
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "open-settings";

    connect() {
        super.connect();
        // Inside the iOS shell now: route through Strada, keeping the other
        // platforms' impls intact.
        if (window.ChippieBridge && window.ChippieBridge.register) {
            window.ChippieBridge.register("openSettings", {
                ios: (name) => {
                    this.send("open", { section: section(name) });
                    return true;
                },
                android: androidOpenSettings,
                pwa: pwaOpenSettings,
            });
        }
    }

    disconnect() {
        registerStandalone();
        super.disconnect();
    }
}
